if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      requestController = undefined;
      requestSequence = 0;
      lifecycleController = undefined;
      initializationErrorLogged = false;

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
        this.quantityForm = this.querySelector('.product-form__quantity');
      }

      connectedCallback() {
        if (this.lifecycleController) return;
        this.lifecycleController = new AbortController();
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
        this.addEventListener('media-gallery:ready', () => this.applyInitialMedia(), {
          signal: this.lifecycleController.signal,
        });

        this.initQuantityHandlers();
        try {
          this.initializeEntryState();
        } catch (error) {
          this.handleInitializationFailure(error);
        }
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.cancelPendingVariantRequest();
        this.lifecycleController?.abort();
        this.lifecycleController = undefined;
        this.onVariantChangeUnsubscriber?.();
        this.onVariantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber = undefined;
      }

      initializeEntryState() {
        if (this.productFormElement?.dataset.hasOnlyDefaultVariant === 'true') {
          this.initializeSingleVariantState();
          this.applyInitialMedia();
          this.revealProductPage('initialized-single-variant');
          return;
        }

        const initialColor = this.resolveInitialColorControl();
        this.clearOptionSelections(initialColor);
        this.setUnresolvedCommerceState(this.classifyPurchaseActionState({ selectionComplete: false }));
        this.applyInitialMedia(initialColor);
        this.revealProductPage('initialized');
        const selection = this.orderedOptionValueIds();
        if (!selection.complete && selection.ids.length) this.renderOptionState(selection, null);
      }

      initializeSingleVariantState() {
        const input = this.productFormElement?.variantIdInput;
        const sourceButton = this.productFormElement?.submitButton;
        const variantId = input?.value || '';
        if (!sourceButton || !/^\d+$/.test(variantId)) {
          throw new Error('Single-variant product omitted its exact purchase contract');
        }
        const variant = { id: variantId, available: !sourceButton.disabled };
        const state = this.classifyPurchaseActionState({
          selectionComplete: true,
          sourceButton,
          variant,
        });
        this.renderPurchaseAction(state, { sourceButton, variant });
        this.querySelector(`#Quantity-Form-${this.dataset.section}`)?.toggleAttribute('hidden', state !== 'available');
      }

      handleInitializationFailure(error) {
        this.dataset.scoreggiaInitializationError = 'true';
        this.setUnresolvedCommerceState(this.classifyPurchaseActionState({ error: true }));
        if (!this.initializationErrorLogged) {
          this.initializationErrorLogged = true;
          console.error('[Scoreggia PDP] Initialization failed; commerce remains disabled.', error);
        }
        this.revealProductPage('initialization-error');
      }

      revealProductPage(reason = 'fallback') {
        if (!this.isConnected || document.querySelector('product-info') !== this) return false;
        this.applyInitialMedia();
        this.dataset.scoreggiaRevealReason = reason;
        document.documentElement.classList.remove('scoreggia-pdp-pending');
        delete document.documentElement.dataset.scoreggiaPdpPending;
        if (window.scoreggiaPdpRevealTimer) {
          window.clearTimeout(window.scoreggiaPdpRevealTimer);
          window.scoreggiaPdpRevealTimer = undefined;
        }
        return true;
      }

      resolveInitialColorControl() {
        const colorControls = Array.from(
          this.querySelectorAll('[data-scoreggia-color-option][data-option-value-id]')
        );
        if (!colorControls.length) return null;

        let handoffId = '';
        try {
          handoffId = new URL(window.location.href).searchParams.get('sg_color') || '';
        } catch {
          handoffId = '';
        }

        const incomingExactColor = colorControls.find((control) =>
          control.matches('option') ? control.selected : control.checked
        );
        const preferredIds = [
          /^\d+$/.test(handoffId) ? handoffId : '',
          incomingExactColor?.dataset.optionValueId || '',
          this.dataset.scoreggiaInitialColorOptionValueId || '',
        ].filter(Boolean);

        for (const preferredId of preferredIds) {
          const match = colorControls.find(
            (control) => String(control.dataset.optionValueId) === String(preferredId)
          );
          if (match) return match;
        }
        return colorControls.find((control) => control.dataset.optionAvailable === 'true') || colorControls[0];
      }

      clearOptionSelections(initialColor) {
        this.variantSelectors?.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = input === initialColor;
        });
        this.variantSelectors?.querySelectorAll('select').forEach((select) => {
          Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
          const colorOption = Array.from(select.options).find((option) => option === initialColor);
          if (colorOption) {
            colorOption.selected = true;
            colorOption.setAttribute('selected', 'selected');
          } else {
            const placeholder = Array.from(select.options).find((option) => !option.dataset.optionValueId);
            select.selectedIndex = placeholder ? placeholder.index : -1;
            placeholder?.setAttribute('selected', 'selected');
          }
        });
        this.updateSelectedValueLabels();
      }

      updateSelectedValueLabels() {
        this.variantSelectors?.querySelectorAll('.product-form__input').forEach((group) => {
          const selected = group.querySelector('input:checked') || group.querySelector('select option:checked');
          const label = group.querySelector('[data-selected-value]');
          if (label && selected) label.textContent = selected.value || '';
        });
      }

      applyInitialMedia(control = null) {
        const selectedColor = control || this.selectedColorControl;
        const mediaId =
          selectedColor?.dataset.scoreggiaFeaturedMediaId || this.dataset.scoreggiaInitialMediaId || '';
        if (!mediaId) return;
        this.mediaGallery?.setActiveMedia?.(`${this.dataset.section}-${mediaId}`, true);
      }

      handleOptionValueChange({ data: { event, target } }) {
        if (!this.contains(event.target)) return;

        if (target?.hasAttribute('data-scoreggia-color-option')) this.applyInitialMedia(target);
        let selection;
        try {
          selection = this.orderedOptionValueIds();
          this.validateActivatedOptionValue(selection, target);
        } catch (error) {
          this.cancelPendingVariantRequest({ settleSelection: false });
          this.setUnresolvedCommerceState(this.classifyPurchaseActionState({ error: true }));
          this.variantSelectors?.rejectPendingSelectPromise(error);
          this.dispatchEvent(
            new CustomEvent('product-info:variant-render-error', { bubbles: true, detail: { error } })
          );
          return;
        }
        this.cancelPendingVariantRequest({ settleSelection: false });
        this.setUnresolvedCommerceState(
          this.classifyPurchaseActionState({
            pending: selection.complete,
            selectionComplete: selection.complete,
          })
        );

        if (!selection.complete) {
          if (selection.ids.length) this.renderOptionState(selection, target?.id);
          else this.variantSelectors?.resolvePendingSelectPromise(null);
          return;
        }
        this.renderOptionState(selection, target?.id);
      }

      orderedOptionValueIds(variantSelects = this.variantSelectors) {
        const controls = Array.from(variantSelects?.querySelectorAll('[data-option-position]') || []);
        const groups = Array.from(
          variantSelects?.querySelectorAll('fieldset, .product-form__input--dropdown') || []
        );
        if (!controls.length || !groups.length) throw new Error('Option controls omitted an option identity');
        if (
          variantSelects === this.variantSelectors &&
          typeof variantSelects.closest === 'function' &&
          (variantSelects.isConnected === false || variantSelects.closest('product-info') !== this)
        ) {
          throw new Error('Option selection came from a foreign or detached product root');
        }

        const optionPositions = [];
        const selectedByPosition = new Map();
        groups.forEach((group) => {
          const groupControls = Array.from(group.querySelectorAll('[data-option-position]') || []);
          const groupPositions = Array.from(
            new Set(
              groupControls.map((control) => Number.parseInt(control.dataset.optionPosition || '', 10))
            )
          );
          if (
            !groupControls.length ||
            groupPositions.length !== 1 ||
            !Number.isInteger(groupPositions[0]) ||
            groupPositions[0] < 1 ||
            optionPositions.includes(groupPositions[0])
          ) {
            throw new Error('Option picker declared an ambiguous option-position contract');
          }
          optionPositions.push(groupPositions[0]);

          const selectedControls = Array.from(
            group.querySelectorAll('input:checked, select option:checked') || []
          );
          selectedControls.forEach((control) => {
            const id = String(control.dataset.optionValueId || '');
            const position = Number.parseInt(control.dataset.optionPosition || '', 10);
            const name = String(control.dataset.optionName || control.name || '').trim();
            const value = String(control.value || '');
            if (
              !/^\d+$/.test(id) ||
              position !== groupPositions[0] ||
              !name ||
              !value ||
              selectedByPosition.has(position) ||
              !controls.includes(control)
            ) {
              throw new Error('Option selection identity is ambiguous');
            }
            selectedByPosition.set(position, Object.freeze({
              position,
              id,
              name,
              value,
              explicitlySelected: true,
            }));
          });
        });
        optionPositions.sort((a, b) => a - b);

        const entries = optionPositions
          .filter((position) => selectedByPosition.has(position))
          .map((position) => selectedByPosition.get(position));
        const ids = entries.map(({ id }) => id);
        const values = entries.map(({ value }) => value);
        const complete = optionPositions.length > 0 && entries.length === optionPositions.length;
        return Object.freeze({
          optionPositions: Object.freeze(optionPositions),
          entries: Object.freeze(entries),
          ids: Object.freeze(ids),
          values: Object.freeze(values),
          complete,
          signature: entries.map(({ position, id }) => `${position}:${id}`).join('|'),
          semanticSignature: entries.map(({ position, name, value }) => `${position}:${name}:${value}`).join('|'),
        });
      }

      validateActivatedOptionValue(selection, target) {
        const id = String(target?.dataset.optionValueId || '');
        const position = Number.parseInt(target?.dataset.optionPosition || '', 10);
        const selectedAtPosition = selection.entries.find((entry) => entry.position === position);
        if (
          !target?.isConnected ||
          target.closest?.('variant-selects') !== this.variantSelectors ||
          !/^\d+$/.test(id) ||
          !Number.isInteger(position) ||
          selectedAtPosition?.id !== id ||
          selectedAtPosition?.value !== String(target.value || '')
        ) {
          throw new Error('Activated option identity does not match the checked selection');
        }
      }

      createOptionRequestIntent(selection, focusId, sequence) {
        if (!selection?.ids?.length || !selection.signature) throw new Error('Option request omitted explicit intent');
        return Object.freeze({
          productId: String(this.dataset.productId || ''),
          sectionId: String(this.sectionId || ''),
          rootId: String(this.id || ''),
          variantSelectsId: String(this.variantSelectors?.id || ''),
          sequence,
          optionPositions: Object.freeze([...selection.optionPositions]),
          entries: Object.freeze(selection.entries.map((entry) => Object.freeze({ ...entry }))),
          ids: Object.freeze([...selection.ids]),
          values: Object.freeze([...selection.values]),
          complete: selection.complete,
          signature: selection.signature,
          semanticSignature: selection.semanticSignature,
          focusId: focusId || '',
        });
      }

      renderOptionState(selection, focusId) {
        const sequence = ++this.requestSequence;
        const intent = this.createOptionRequestIntent(selection, focusId, sequence);
        const controller = new AbortController();
        this.requestController = controller;
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('section_id', intent.sectionId);
        url.searchParams.set('option_values', intent.ids.join(','));

        fetch(`${url.pathname}${url.search}`, {
          signal: controller.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Option rendering failed (${response.status})`);
            return response.text();
          })
          .then((responseText) => {
            if (controller.signal.aborted || sequence !== this.requestSequence || !this.isConnected) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (intent.complete) this.commitExactOptionState(html, intent);
            else this.commitPartialOptionAvailability(html, intent);
          })
          .catch((error) => {
            if (error.name === 'AbortError' || controller.signal.aborted || sequence !== this.requestSequence) return;
            this.setUnresolvedCommerceState(this.classifyPurchaseActionState({ error: true }));
            this.variantSelectors?.rejectPendingSelectPromise(error);
            this.dispatchEvent(
              new CustomEvent('product-info:variant-render-error', { bubbles: true, detail: { error } })
            );
          })
          .finally(() => {
            if (sequence === this.requestSequence) this.requestController = undefined;
          });
      }

      validateResponseProductInfo(html, intent) {
        const sourceProductInfo = html.querySelector('product-info');
        if (
          !sourceProductInfo ||
          String(sourceProductInfo.dataset.productId) !== intent.productId ||
          String(sourceProductInfo.dataset.section) !== intent.sectionId
        ) {
          throw new Error('Option rendering returned the wrong product or section');
        }
        return sourceProductInfo;
      }

      validateResponseOptionValueIdentity(sourceVariantSelects, intent, exact) {
        const responseControls = Array.from(sourceVariantSelects.querySelectorAll('[data-option-position]'));
        const responsePositions = Array.from(
          new Set(
            responseControls.map((control) => Number.parseInt(control.dataset.optionPosition || '', 10))
          )
        ).sort((a, b) => a - b);
        if (
          responsePositions.length !== intent.optionPositions.length ||
          responsePositions.some((position, index) => position !== intent.optionPositions[index])
        ) {
          throw new Error('Option rendering changed the option-position contract');
        }

        intent.entries.forEach(({ position, id }) => {
          const matchingControls = sourceVariantSelects.querySelectorAll(
            `[data-option-position="${position}"][data-option-value-id="${CSS.escape(id)}"]`
          );
          if (matchingControls.length !== 1) {
            throw new Error('Option rendering omitted the requested ProductOptionValue identity');
          }
        });

        if (!exact) return null;
        const responseSelection = this.orderedOptionValueIds(sourceVariantSelects);
        if (
          !responseSelection.complete ||
          responseSelection.signature !== intent.signature ||
          responseSelection.semanticSignature !== intent.semanticSignature
        ) {
          throw new Error('Option rendering substituted a different exact option-value sequence');
        }
        return responseSelection;
      }

      validateExactVariantIdentity(variant, intent, responseSelection) {
        if (!variant) return;
        const variantOptions = Array.isArray(variant.options) ? variant.options.map(String) : [];
        if (
          !/^\d+$/.test(String(variant.id || '')) ||
          variantOptions.length !== intent.values.length ||
          variantOptions.some((value, index) => value !== intent.values[index]) ||
          responseSelection.semanticSignature !== intent.semanticSignature
        ) {
          throw new Error('Exact Shopify variant options do not match the customer request intent');
        }
      }

      commitPartialOptionAvailability(html, intent) {
        const sourceProductInfo = this.validateResponseProductInfo(html, intent);
        const sourceVariantSelects = sourceProductInfo.querySelector('variant-selects');
        const currentVariantSelects = this.variantSelectors;
        if (!sourceVariantSelects || !currentVariantSelects) {
          throw new Error('Option rendering omitted ProductOptionValue controls');
        }
        this.validateResponseOptionValueIdentity(sourceVariantSelects, intent, false);

        const explicitIds = new Set(intent.ids);
        currentVariantSelects.querySelectorAll('[data-option-value-id]').forEach((control) => {
          const optionValueId = String(control.dataset.optionValueId || '');
          if (!optionValueId) return;
          const source = sourceVariantSelects.querySelector(
            `[data-option-value-id="${CSS.escape(optionValueId)}"]`
          );
          if (!source) return;
          const available = source.dataset.optionAvailable === 'true';
          control.dataset.optionAvailable = String(available);
          control.classList.toggle('disabled', !available);
          control.setAttribute('aria-disabled', String(!available));
        });

        currentVariantSelects.querySelectorAll('input[type="radio"]').forEach((control) => {
          control.checked = explicitIds.has(String(control.dataset.optionValueId || ''));
        });
        currentVariantSelects.querySelectorAll('select').forEach((select) => {
          const selected = Array.from(select.options).find((option) =>
            explicitIds.has(String(option.dataset.optionValueId || ''))
          );
          if (selected) select.value = selected.value;
          else {
            const placeholder = Array.from(select.options).find((option) => !option.dataset.optionValueId);
            select.selectedIndex = placeholder ? placeholder.index : -1;
          }
        });
        this.updateSelectedValueLabels();
        this.setUnresolvedCommerceState(this.classifyPurchaseActionState({ selectionComplete: false }), {
          statusSourceRoot: sourceProductInfo,
        });
        currentVariantSelects.resolvePendingSelectPromise(null);
        if (intent.focusId) this.querySelector(`#${CSS.escape(intent.focusId)}`)?.focus({ preventScroll: true });
      }

      commitExactOptionState(html, intent) {
        const sourceProductInfo = this.validateResponseProductInfo(html, intent);
        const sourceVariantSelects = sourceProductInfo?.querySelector('variant-selects');
        if (!sourceVariantSelects) throw new Error('Option rendering omitted ProductOptionValue controls');
        const responseSelection = this.validateResponseOptionValueIdentity(sourceVariantSelects, intent, true);

        const variant = this.getSelectedVariant(sourceProductInfo);
        this.validateExactVariantIdentity(variant, intent, responseSelection);
        this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);
        this.variantSelectors.replaceWith(sourceVariantSelects);
        const appliedSelection = this.orderedOptionValueIds();
        if (
          appliedSelection.signature !== intent.signature ||
          appliedSelection.semanticSignature !== intent.semanticSignature
        ) {
          throw new Error('Applied option controls do not match the customer request intent');
        }

        this.copyCommerceContent(html, 'Sku');
        this.copyCommerceContent(html, 'Inventory');
        this.copyCommerceContent(html, 'Volume');
        this.copyCommerceContent(html, 'Price-Per-Item');
        this.copyCommerceContent(html, 'Quantity-Rules');
        this.copyCommerceContent(html, 'Volume-Note');
        this.copyQuantityForm(html);

        if (!variant) {
          const sourceButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const state = this.classifyPurchaseActionState({
            selectionComplete: true,
            sourceButton,
            variant,
          });
          this.setUnresolvedCommerceState(state, { sourceButton, statusSourceRoot: sourceProductInfo });
          if (intent.focusId) this.querySelector(`#${CSS.escape(intent.focusId)}`)?.focus({ preventScroll: true });
          return;
        }

        this.commitProductForm(html, variant);
        this.renderExactPriceStatus(sourceProductInfo);
        this.pickupAvailability?.update(variant);
        const mediaId = variant.featured_media?.id || variant.featured_image?.id;
        if (mediaId) this.mediaGallery?.setActiveMedia?.(`${this.dataset.section}-${mediaId}`, true);
        this.updateURL(variant.id);

        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.sectionId, html, variant },
        });
        this.dispatchEvent(
          new CustomEvent('product-info:variant-rendered', { bubbles: true, detail: { variant } })
        );
        if (intent.focusId) this.querySelector(`#${CSS.escape(intent.focusId)}`)?.focus({ preventScroll: true });
      }

      copyCommerceContent(html, id) {
        const source = html.getElementById(`${id}-${this.sectionId}`);
        const destination = this.querySelector(`#${id}-${this.dataset.section}`);
        if (!source || !destination) return;
        destination.innerHTML = source.innerHTML;
        destination.className = source.className;
        destination.toggleAttribute('hidden', source.hasAttribute('hidden'));
      }

      copyQuantityForm(html) {
        const source = html.getElementById(`Quantity-Form-${this.sectionId}`);
        const destination = this.querySelector(`#Quantity-Form-${this.dataset.section}`);
        if (!source || !destination) return;
        destination.innerHTML = source.innerHTML;
        destination.toggleAttribute('hidden', source.hasAttribute('hidden'));
        this.quantityForm = destination;
        this.quantityInput = destination.querySelector('.quantity__input');
        this.setQuantityBoundries();
      }

      commitProductForm(html, variant) {
        const sourceButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!sourceButton) throw new Error('Option rendering omitted the purchase button');
        const state = this.classifyPurchaseActionState({
          selectionComplete: true,
          sourceButton,
          variant,
        });
        this.renderPurchaseAction(state, { sourceButton, variant });
        this.querySelector(`#Quantity-Form-${this.dataset.section}`)?.toggleAttribute('hidden', state !== 'available');
      }

      classifyPurchaseActionState({ error = false, pending = false, selectionComplete = false, sourceButton, variant } = {}) {
        if (error) return 'error';
        if (!selectionComplete) return 'incomplete';
        if (pending) return 'loading';
        if (!variant) return 'invalid';
        if (!sourceButton) return 'error';
        return variant.available === false || sourceButton.hasAttribute('disabled') ? 'sold-out' : 'available';
      }

      renderPurchaseAction(state, { sourceButton = null, variant = null } = {}) {
        const allowedStates = new Set(['incomplete', 'loading', 'available', 'sold-out', 'invalid', 'error']);
        let resolvedState = allowedStates.has(state) ? state : 'error';
        const action = this.purchaseAction;
        const button = this.productFormElement?.submitButton || this.querySelector('.product-form__submit');
        const input = this.productFormElement?.variantIdInput || this.nativeProductForm?.querySelector('[name="id"]');
        const exactVariantId = variant?.id == null ? '' : String(variant.id);
        const exactState = resolvedState === 'available' || resolvedState === 'sold-out';
        const sourceMarkup = sourceButton?.innerHTML || '';

        if (
          !action ||
          !button ||
          !input ||
          (exactState && (!/^\d+$/.test(exactVariantId) || !sourceButton)) ||
          (resolvedState === 'invalid' && !sourceButton)
        ) {
          resolvedState = 'error';
        }

        const hideAction = ['incomplete', 'loading', 'error'].includes(resolvedState);
        const disableAction = resolvedState !== 'available';

        input.disabled = true;
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.classList.remove('loading');

        if (exactState && resolvedState !== 'error') input.value = exactVariantId;
        else input.value = '';

        if (!hideAction && sourceMarkup) button.innerHTML = sourceMarkup;
        else if (hideAction) button.innerHTML = '';

        button.hidden = hideAction;
        button.setAttribute('aria-hidden', String(hideAction));
        button.querySelector('.loading__spinner')?.classList.add('hidden');
        if (this.productFormElement) this.productFormElement.submitButtonText = button.querySelector('span');

        action?.toggleAttribute('hidden', hideAction);
        action?.setAttribute('aria-hidden', String(hideAction));
        if (action) action.dataset.scoreggiaPurchaseState = resolvedState;

        action?.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout').forEach((control) => {
          const hideDynamicCheckout = resolvedState !== 'available';
          control.toggleAttribute('hidden', hideDynamicCheckout);
          control.setAttribute('aria-hidden', String(hideDynamicCheckout));
        });

        button.disabled = disableAction;
        button.setAttribute('aria-disabled', String(disableAction));
        input.disabled = disableAction;
        this.dataset.scoreggiaCommerceState = resolvedState;
        if (exactState && resolvedState !== 'error') this.dataset.scoreggiaExactVariantId = exactVariantId;
        else delete this.dataset.scoreggiaExactVariantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return resolvedState;
      }

      setUnresolvedCommerceState(state, purchase = {}) {
        try {
          const resolvedState = this.renderPurchaseAction(state, purchase);
          if (resolvedState === 'incomplete') {
            this.renderBaselinePriceStatus(purchase.statusSourceRoot || this);
          } else if (resolvedState === 'invalid') {
            this.renderUnavailablePriceStatus(purchase.statusSourceRoot || this);
          }
          ['Inventory', 'Sku', 'Quantity-Rules', 'Volume-Note', 'Volume', 'Price-Per-Item'].forEach((id) => {
            const fragment = this.querySelector(`#${id}-${this.dataset.section}`);
            if (!fragment) return;
            fragment.setAttribute('hidden', '');
            fragment.innerHTML = '';
          });
          this.resetNeutralQuantityState();
          this.pickupAvailability?.removeAttribute('available');
          this.pickupAvailability?.removeAttribute('data-variant-id');
        } catch (error) {
          this.dataset.scoreggiaIncompleteStateError = 'true';
          try {
            this.renderPurchaseAction(this.classifyPurchaseActionState({ error: true }));
          } catch {}
        }
        try {
          this.clearProductFormError();
        } catch {
          this.dataset.scoreggiaProductFormHelperError = 'true';
        }
      }

      copyPriceStatusSource(sourceRoot, selector) {
        const destination = this.querySelector(`#price-${this.dataset.section}`);
        const source = sourceRoot?.querySelector?.(selector);
        if (!destination || !source?.content?.childElementCount) {
          throw new Error('Product status source is unavailable');
        }
        destination.replaceChildren(source.content.cloneNode(true));
        destination.removeAttribute('hidden');
        return true;
      }

      renderBaselinePriceStatus(sourceRoot) {
        return this.copyPriceStatusSource(sourceRoot, '[data-scoreggia-baseline-price-source]');
      }

      renderExactPriceStatus(sourceRoot) {
        return this.copyPriceStatusSource(sourceRoot, '[data-scoreggia-exact-status-source]');
      }

      renderUnavailablePriceStatus(sourceRoot) {
        return this.copyPriceStatusSource(sourceRoot, '[data-scoreggia-unavailable-status-source]');
      }

      resetNeutralQuantityState() {
        const quantityForm = this.querySelector(`#Quantity-Form-${this.dataset.section}`);
        const quantityInput = quantityForm?.querySelector('.quantity__input');
        if (!quantityForm || !quantityInput) return;
        quantityInput.min = '1';
        quantityInput.step = '1';
        quantityInput.value = '1';
        quantityInput.removeAttribute('max');
        quantityInput.removeAttribute('data-max');
        quantityInput.dataset.min = '1';
        quantityForm.hidden = true;
      }

      clearProductFormError() {
        const productFormElement = this.productFormElement;
        if (
          !productFormElement ||
          !productFormElement.isConnected ||
          typeof productFormElement.handleErrorMessage !== 'function'
        ) {
          return false;
        }
        try {
          productFormElement.handleErrorMessage();
          return true;
        } catch {
          this.dataset.scoreggiaProductFormHelperError = 'true';
          return false;
        }
      }

      cancelPendingVariantRequest({ settleSelection = true } = {}) {
        this.requestSequence += 1;
        this.requestController?.abort();
        this.requestController = undefined;
        if (settleSelection) this.variantSelectors?.resolvePendingSelectPromise(null);
      }

      updateURL(variantId) {
        const url = new URL(this.dataset.url, window.location.origin);
        if (variantId) url.searchParams.set('variant', String(variantId));
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${url.pathname}${url.search}`);
        if (this.dataset.updateUrl !== 'false') {
          window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
        }
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(productInfoNode?.querySelector('variant-selects'), '[data-selected-variant]');
      }

      initQuantityHandlers() {
        if (!this.quantityInput || !this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;
        const cartQuantity = Number.parseInt(this.quantityInput.dataset.cartQuantity || '0', 10);
        const configuredMin = Number.parseInt(this.quantityInput.dataset.min || '1', 10);
        const configuredMax = this.quantityInput.dataset.max
          ? Number.parseInt(this.quantityInput.dataset.max, 10)
          : null;
        const step = Number.parseInt(this.quantityInput.step || '1', 10);
        const max = configuredMax === null ? null : configuredMax - cartQuantity;
        let min = max === null ? configuredMin : Math.min(configuredMin, max);
        if (cartQuantity >= configuredMin) min = Math.min(min, step);
        this.quantityInput.min = min;
        if (max !== null) this.quantityInput.max = max;
        else this.quantityInput.removeAttribute('max');
        this.quantityInput.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const variantId = this.productFormElement?.variantIdInput?.value;
        if (!variantId) return;
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.copyQuantityForm(html);
            this.copyCommerceContent(html, 'Quantity-Rules');
          })
          .catch((error) => console.error(error))
          .finally(() => spinner?.classList.add('hidden'));
      }

      get productFormElement() {
        return this.querySelector('product-form');
      }

      get purchaseAction() {
        return this.productFormElement?.querySelector('[data-scoreggia-purchase-action]') || null;
      }

      get nativeProductForm() {
        return this.productFormElement?.querySelector('form[data-type="add-to-cart-form"]') || null;
      }

      get pickupAvailability() {
        return this.querySelector('pickup-availability');
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get mediaGallery() {
        return this.querySelector('media-gallery');
      }

      get selectedColorControl() {
        return this.querySelector(
          '[data-scoreggia-color-option][data-option-value-id]:checked, option[data-scoreggia-color-option][data-option-value-id]:checked'
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
